import {createGrid} from "../../grids";
import {waitUntil} from "../../../waitUntil";

// noinspection JSUnusedGlobalSymbols
/**
 * Creates a grid to display a spreadsheet file contents
 *
 * @param {Element} domElement
 * @param {import('./spreadsheetGrids').SpreadsheetGrid.FileMetaData} fileMetaData
 * @param {string} rowsUrl
 * @param {import('./spreadsheetGrids').SpreadsheetGrid.SpreadSheetGridOptions} fileGridOptions
 * @param {import('ag-grid-community/dist/lib/entities/gridOptions').GridOptions | Object} [gridWrapperOptions] pass through to createGrid wrapper
 * @return {import('../../grids').GridInstance}
 */
function createSpreadsheetGrid(
	domElement,
	fileMetaData,
	rowsUrl,
	fileGridOptions = {
		withColumnSelects: true,
		columnSelectOptions: [],

	},
	gridWrapperOptions = {},
) {
	// Build columns definition
	const columnDefs = [{
		// Row index column
		colId: '-1',
		field: 'displayIndex',
		headerName: '#',
		maxWidth: 80
	}];

	let grid;

	const getRowMergedCellsArray = params => {
		// noinspection JSUnresolvedVariable
		return params.data.mergedCells ? params.data.mergedCells : [];
	}

	const rowSpanResolver = (params) => {
		const mergedCells = getRowMergedCellsArray(params);
		return mergedCells[params.colDef.colId] ? mergedCells[params.colDef.colId].height : 1;
	}

	const colSpanResolver = (params) => {
		const mergedCells = getRowMergedCellsArray(params);
		return mergedCells[params.colDef.colId] ? mergedCells[params.colDef.colId].width : 1;
	}

	let columnHeaderOptions = [
		{
			value: '',
			text: '- - - - - -',
		},
	];

	if (fileGridOptions.columnSelectOptions) {
		columnHeaderOptions = columnHeaderOptions.concat(fileGridOptions.columnSelectOptions);
	}

	fileMetaData.headerData.forEach((header, index) => {
		// noinspection JSUnresolvedVariable,JSUnusedGlobalSymbols
		const colDefinition = {
			colId: index,
			field: header.index.toString(),
			headerName: header.letter,
			minWidth:
				180 // Select Width
				+
				(header.letter.length * 12) // Letter Width
			,
			rowSpan: rowSpanResolver,
			colSpan: colSpanResolver,
			cellClass: params => getCellClass(params, index),
		}

		if (fileGridOptions.withColumnSelects) {
			// Add custom header template
			const selectName = `column-select-${index}`;

			const selectOptions = columnHeaderOptions.map(optionData => {
				return `<option value="${optionData.value}">${optionData.text}</option>`
			}).join("\n");

			const columnSelect = `
				<select class="column-header-select" data-column-index="${index}" name="${selectName}">
					${selectOptions}
				</select>
			`

			// noinspection HtmlUnknownAttribute
			colDefinition.headerComponentParams = {
				template: `
					<div class="ag-cell-label-container" role="presentation">
						<span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
						<div ref="eLabel" class="ag-header-cell-label" role="presentation">
							<span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
							<span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
							<span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
							<span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
							<span ref="eText" class="ag-header-cell-text" role="columnheader"></span>
							<span class="column-header-select-container ml-3">${columnSelect}</span>
							<span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
						</div>
					</div>
				`,
			};

			// Wait until grid has been initialized to set up handler
			waitUntil(
				() => domElement.querySelector(`select[name=${selectName}]`)
			).then(() => {
				const select = domElement.querySelector(`select[name=${selectName}]`);

				// When selected, clear current non empty selection from other selects
				select.addEventListener('change', () => {
					if (select.value) {
						// Something was selected, check other selects elements
						domElement.querySelectorAll('.column-header-select')
							.forEach(otherSelect => {
								if (select !== otherSelect && otherSelect.value === select.value) {
									// Another select had the same value, clear it
									otherSelect.value = '';
								}
							})
					}

					// Update metadata
					if (fileMetaData.format) {
						// Get all
						fileMetaData.format.columnsMap = {};
						domElement.querySelectorAll('.column-header-select')
							.forEach(select => {
								if (select.value) {
									fileMetaData.format.columnsMap[select.dataset.columnIndex] = select.value;
								}
							})
					}

					// Refresh cells
					grid.api.refreshCells({
						force: true,
					});
				})
			})
		}

		// noinspection JSCheckFunctionSignatures
		columnDefs.push(colDefinition)
	})

	let gridGetRowClass = getRowClass;

	if (fileGridOptions.customGetRowClass) {
		gridGetRowClass = fileGridOptions.customGetRowClass;
	}

	grid = createGrid(
		domElement,
		{
			columnDefs,
			pagination: false,
			_rowName: 'Rows',
			paginationPageSize: 5000,
			cacheBlockSize: 5000,
			rowBuffer: 50,
			suppressRowTransform: false,
			suppressColumnVirtualisation: true,
			suppressMaxRenderedRowRestriction: true,

			getRowClass: gridGetRowClass,
			...gridWrapperOptions
		},
		rowsUrl,
	);

	// Add custom methods
	grid.setColumnSelectValues = setColumnSelectValues;

	return grid;

	// Methods

	/**
	 * Handle retrieving the class for the whole row
	 *
	 * @param params
	 */
	function getRowClass(params) {
		const rowClasses = [];

		// Retrieve data rows thresholds
		const dataStartRow = (fileMetaData.format.dataStartRow ? fileMetaData.format.dataStartRow : 1);
		const dataEndRow = (fileMetaData.format.dataEndRow ? fileMetaData.format.dataEndRow : Number.MAX_SAFE_INTEGER);

		const rowIndex = parseInt(params.data.index) + 1;

		if (rowIndex < dataStartRow || rowIndex > dataEndRow) {
			rowClasses.push('ignored');
		}

		return rowClasses;
	}

	/**
	 * Returns the cell class, used to highlight selected columns
	 *
	 * @param params
	 * @param columnIndex
	 */
	function getCellClass(params, columnIndex) {
		const cellClasses = [];

		// Check if header select has a value
		const headerSelect = domElement.querySelector(`.column-header-select[data-column-index="${columnIndex}"]`);
		if (headerSelect && headerSelect.value) {
			cellClasses.push('selected-column');
		}

		return cellClasses;
	}

	/**
	 * The new format, if missing the given metadata object format will be used
	 * @param columnsMap
	 */
	function setColumnSelectValues(columnsMap = null) {
		if (columnsMap === null) {
			columnsMap = fileMetaData.format.columnsMap;
		}
		// Clear all selects
		domElement.querySelectorAll('.column-header-select')
			.forEach(select => select.value = '')

		// Select only the given ones
		Object.keys(columnsMap).forEach(columnIndex => {
			const columnSelect = domElement.querySelector(
				`.column-header-select[data-column-index="${columnIndex}"]`
			);

			if (columnSelect) {
				columnSelect.value = columnsMap[columnIndex];
			}
		})
	}
}

export {createSpreadsheetGrid}
